import { SaveGroupRequest } from '@/client/groups';
import { LoadingStateType } from '@/common/constants';
import { FormikSwitch } from '@/components/form';
import { FormikInput } from '@/components/form/FormikInput';
import { groupSchema } from '@/components/groups/forms/validations';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FormContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode } from '@/utils/helpers';
import { Field, Form, Formik, FormikValues } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

type GroupFormProps = {
  initialValues: SaveGroupRequest;
  state?: LoadingStateType;
  onSubmit: (data: FormikValues) => void;
  disabled?: boolean;
};

export const GroupForm: React.FC<GroupFormProps> = ({
  initialValues,
  state,
  onSubmit,
  disabled,
}) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={groupSchema(t)}
      onSubmit={onSubmit}
    >
      {({ errors }) => (
        <FormContainer>
          <Form className="w-full" autoComplete="off">
            <div className="field w-full mb-0">
              <Field
                id="name"
                name="name"
                label={t('generic.name')}
                className="w-full"
                component={FormikInput}
                placeholder={t('group.name')}
                required={!initialValues?.name}
                disabled={disabled}
              />
            </div>
            {!initialValues?.isForPhishing && (
              <div className="field-checkbox mt-3">
                <Field
                  inputId="autoAssign"
                  name="autoAssign"
                  label={t('group.autoAssign')}
                  component={FormikSwitch}
                  disabled={disabled}
                />
              </div>
            )}
            <AppButton
              isSubmit={!isDemoAccount}
              severity="secondary"
              label={initialValues.name ? t('button.save') : t('button.create')}
              className="w-4 mt-4 mb-4"
              state={state}
              isDisabled={!!Object.keys(errors).length || disabled}
            />
          </Form>
        </FormContainer>
      )}
    </Formik>
  );
};
